import { Form, Input, Spin, Table } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Button from '../../components/Button/Button';
import { useContactStore } from '../../hooks/useContactStore';
import { apiInstance } from '../../utils/api';

const EmployeesTable = () => {
  const { push } = useHistory();
  const { currentContactStore } = useContactStore();

  const [searchText, setSearchText] = useState('');
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [cards, setCards] = useState([]);
  const [requests, setRequests] = useState([]); // заявки на виготовлення карт

  const fetchEmployees = async () => {
    setLoadingEmployees(true); // Start loading
    try {
      // Выполняем все запросы параллельно (requests может отсутствовать на бекенде, поэтому оборачиваем в catch)
      const employeesPromise = apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=contacts`,
        'GET'
      );
      const cardsPromise = apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=cards`,
        'GET'
      );
      const requestsPromise = apiInstance(
        `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=requests`,
        'GET'
      ).catch(() => ({ rows: [] }));

      const [employeessResponse, cardsResponse, requestsResponse] = await Promise.all([
        employeesPromise,
        cardsPromise,
        requestsPromise,
      ]);

      const contactsRaw = employeessResponse.rows || [];
      const cardsRaw = cardsResponse.rows || [];

      const cardsById = cardsRaw.reduce((acc, card) => {
        if (card?.Id) {
          acc.set(card.Id, card);
        }
        return acc;
      }, new Map());

      // Точечный запрос карт по contactId в параллели для 100% точности (как в карточке сотрудника)
      const contactCardsPromises = contactsRaw.map((contact) =>
        apiInstance(
          `${process.env.REACT_APP_API_URL}/api/crm/get-data?type=contact-card&contactId=${contact.Id}`,
          'GET'
        )
          .then((res) => (res.rows && res.rows.length ? res.rows[0] : null))
          .catch(() => null)
      );

      const contactCards = await Promise.all(contactCardsPromises);
      const contactCardsMap = new Map();
      contactsRaw.forEach((contact, idx) => {
        const cCard = contactCards[idx];
        if (cCard) {
          contactCardsMap.set(contact.Id.toLowerCase(), cCard);
        }
      });

      const normalizeCardLink = (cardLike) => {
        if (!cardLike) return { id: '', number: '' };
        if (typeof cardLike === 'string') {
          return { id: cardLike, number: '' };
        }
        if (typeof cardLike === 'object') {
          return {
            id: cardLike.value || cardLike.Id || '',
            number: cardLike.displayValue || cardLike.Name || cardLike.cardNumber || '',
          };
        }
        return { id: '', number: '' };
      };

      const employeesWithCards = contactsRaw.map((contact) => {
        const { id: linkedCardId, number: linkedCardNumber } = normalizeCardLink(
          contact?.RespCardSCA
        );

        // Сначала берем карту из точечного запроса (100% сопоставление)
        let card = contactCardsMap.get(contact.Id.toLowerCase());

        // Если точечный запрос ничего не вернул, пробуем найти по обратной связи в cardsRaw
        if (!card) {
          const matchedCardByContact = cardsRaw.find(
            (cardItem) =>
              cardItem?.RespContact?.value &&
              contact.Id &&
              cardItem.RespContact.value.toLowerCase() === contact.Id.toLowerCase()
          );
          card = matchedCardByContact || (linkedCardId ? cardsById.get(linkedCardId) : undefined);
        }

        const isBlocked = card?.RespSCAStatus?.value === '25405d2a-9df4-47a9-9313-fc8b4ca329fa';
        const isFree = card?.RespSCAStatus?.value === 'df17a6d7-2510-4a1e-9692-f9c34d66b156';
        const isNotAssigned = !card || isBlocked || isFree || !card?.RespSCAStatus?.value;

        if (isNotAssigned) {
          card = undefined;
        }
        const cardStatus = card?.RespSCAStatus || null;

        return {
          ...contact,
          RespName: contact.GivenName || contact.RespName || '',
          RespSurName: contact.Surname || contact.RespSurName || '',
          RespMobilePhone: contact.MobilePhone || contact.RespMobilePhone || '',
          RespCardNumber: isNotAssigned
            ? ''
            : (card?.Name && card.Name !== 'Картка СКД'
               ? card.Name
               : (linkedCardNumber && linkedCardNumber !== 'Картка СКД' ? linkedCardNumber : '')),
          RespCardId: isNotAssigned ? '' : (card?.Id || linkedCardId || ''),
          RespCardStatus: cardStatus,
        };
      });

      setEmployees(employeesWithCards);
      setCards(cardsRaw);
      setRequests(requestsResponse.rows || []);
    } catch (error) {
      console.error('Ошибка получения данных:', error);
    } finally {
      setLoadingEmployees(false); // Stop loading
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, [currentContactStore]);

  const filteredEmployees = useMemo(() => {
    if (!searchText) return employees;

    const searchLower = searchText.toLowerCase();
    return employees.filter((employee) => {
      const fullName = `${employee.RespName || ''} ${employee.RespSurName || ''}`.toLowerCase();
      const phone = (employee.RespMobilePhone || '').toLowerCase();
      return fullName.includes(searchLower) || phone.includes(searchLower);
    });
  }, [employees, searchText]);

  const formatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    return `+${cleaned.slice(0, 2)} (${cleaned.slice(2, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(
      8,
      10
    )}-${cleaned.slice(10, 12)}`;
  };

  const columns = [
    {
      title: 'Співробітник',
      key: 'fullName',
      className: 'start',
      width: 150,
      render: (_, record) => (
        <div className="f16-grey-dark">{`${record.RespName} ${record.RespSurName}`}</div>
      ),
    },
    {
      title: 'Номер телефону',
      dataIndex: 'RespMobilePhone',
      key: 'RespMobilePhone',
      className: 'center f16-grey-dark',
      width: 150,
      render: (phoneNumber) => <div>{formatPhoneNumber(phoneNumber)}</div>,
    },
    {
      title: 'Номер картки',
      dataIndex: 'RespCardNumber',
      key: 'RespCardNumber',
      className: 'center',
      width: 200,
      render: (cardNumber) => <div className="f16-grey-dark">{cardNumber || 'Не призначено'}</div>,
    },
    {
      title: 'Статус',
      key: 'RespCardStatus',
      className: 'center f16-grey-dark',
      width: 170,
      render: (_, record) => {
        // Логіка визначення чи картка замовлена (але ще не видана / не відхилена)
        // Ищем по полю RespCardReceiver (которое мы устанавливаем при создании заявки)
        const isCardOrdered = requests.some(
          (request) =>
            record?.Id &&
            ((request?.RespCardReceiver?.value && request.RespCardReceiver.value === record.Id) ||
             (Array.isArray(request?.RespCardReceivers) && request.RespCardReceivers.includes(record.Id))) &&
            request?.Status?.value !== '6e5f4218-f46b-1410-fe9a-0050ba5d6c38' && // Відхилено?
            request?.Status?.value !== '3e7f420c-f46b-1410-fc9a-0050ba5d6c38' // Скасовано?
        );

        if (isCardOrdered && !record?.RespCardNumber) {
          return <div style={{ color: '#DB3EB1' }}>Картка замовлена</div>;
        }

        const status = record?.RespCardStatus;
        const statusConfig = {
          '3075a30b-f6e8-45cd-990b-28399db75400': {
            text: 'Видана',
            color: '#52c41a',
          },
          '2ec4e1b9-b7f5-4eb2-9a83-695c9b1be716': {
            text: 'Готова до видачі',
            color: '#1890ff',
          },
          '25405d2a-9df4-47a9-9313-fc8b4ca329fa': {
            text: 'Заблокована',
            color: '#ff4d4f',
          },
        };

        const currentStatus = status?.value
          ? statusConfig[status.value] || { text: 'Невідомо', color: '#8c8c8c' }
          : { text: 'Не призначено', color: '#8c8c8c' };

        return <div style={{ color: currentStatus.color }}>{currentStatus.text}</div>;
      },
    },
    {
      title: 'Дії',
      key: 'actions',
      className: 'center',
      width: 150,
      render: (_, record) => (
        <Button
          text="Редагувати"
          bg="orange"
          onClick={() => push(`/employees/edit/${record.Id}`)}
        />
      ),
    },
  ];

  const targetId = '2ec4e1b9-b7f5-4eb2-9a83-695c9b1be716';
  const BLOCKED_STATUS_ID = '25405d2a-9df4-47a9-9313-fc8b4ca329fa';

  const targetCount = cards.filter((card) => card.RespSCAStatus?.value === targetId).length;

  // Фильтруем карты исключая заблокированные
  const allCards = cards.filter((card) => card.RespSCAStatus?.value !== BLOCKED_STATUS_ID).length;

  if (loadingEmployees) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="employees-page">
      <div className="box header-content">
        <div className="left-section">
          <div className="search-and-filter">
            <Form.Item name="searchEmployee" style={{ marginBottom: 0, width: 300 }}>
              <Input
                placeholder="Пошук за ім'ям або телефоном"
                className="search-input"
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Form.Item>
            <div style={{ marginLeft: '16px' }}>
              <Button
                text="Додати співробітника"
                bg="pink"
                onClick={() => push('/employees/create')}
              />
            </div>
          </div>
        </div>
        <div className="right-section">
          <div className="cards-available">
            <span className="text-grey-dark-normal-f22-l28">Доступно карток: </span>
            <span className="text-black-bold-f24">
              <span style={{ color: 'rgb(219, 62, 177)' }}>{targetCount}</span> з {allCards}
            </span>
          </div>
        </div>
      </div>

      <div className="box">
        <Table
          className="default-table account-table request-table employees-clickable-rows"
          dataSource={filteredEmployees}
          columns={columns}
          rowKey="Id"
          pagination={{
            pageSize: 10,
            total: filteredEmployees?.length,
            showSizeChanger: false,
          }}
          locale={{ emptyText: 'Ще немає даних для відображення' }}
          loading={loadingEmployees}
          onRow={(record) => ({
            onClick: () => push(`/employees/edit/${record.Id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </div>
    </div>
  );
};

export default EmployeesTable;
